import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { FiArrowUpCircle, FiCreditCard, FiHome, FiLogOut, FiMoreHorizontal, FiRefreshCw, FiSettings, FiTrendingUp, FiUser, FiX } from 'react-icons/fi';
import { MdSwapHoriz } from 'react-icons/md';
import { SHOW_USDT_WITHDRAW_MENU } from '../featureFlags';
import { useAuth } from '../auth/AuthContext';
import { formatAmount, formatDate, getContrastYIQ } from '../utils/formatters';
import adaboCashLogo from '../image/adabo_cash_logo.png';

const pathToMenu = {
  '/': 'dashboard',
  '/dashboard': 'dashboard',
  '/deposit': 'deposit',
  '/withdraw': 'withdraw',
  '/cards': 'cards',
  '/transactions': 'transactions',
  '/exchange': 'exchange',
  '/contact': 'contact',
  '/verification': 'verification',
  '/account-settings': 'settings',
};

export function ProtectedLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, setUser, user } = useAuth();
  const [activeMenu, setActiveMenu] = useState(() => sessionStorage.getItem('activeMenu') || 'dashboard');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cardsPageKey, setCardsPageKey] = useState(0);
  const mobileMoreButtonRef = useRef(null);
  const mobileMoreCloseRef = useRef(null);
  const mobileMoreSheetRef = useRef(null);
  const isMoneyFlowPage = ['/deposit', '/exchange', '/withdraw'].includes(location.pathname);
  const isMoreActive = mobileMenuOpen || ['withdraw', 'transactions', 'contact', 'settings'].includes(activeMenu);

  useEffect(() => {
    const nextMenu = pathToMenu[location.pathname];
    if (nextMenu) {
      setActiveMenu(nextMenu);
    }
  }, [location.pathname]);

  useEffect(() => {
    sessionStorage.setItem('activeMenu', activeMenu);
  }, [activeMenu]);

  useEffect(() => {
    if (!dropdownOpen) return undefined;
    const handleClick = (event) => {
      if (!event.target.closest('.user-info')) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [dropdownOpen]);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const triggerButton = mobileMoreButtonRef.current;
    document.body.style.overflow = 'hidden';
    mobileMoreCloseRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMobileMenuOpen(false);
        return;
      }

      if (event.key !== 'Tab' || !mobileMoreSheetRef.current) return;
      const focusable = mobileMoreSheetRef.current.querySelectorAll('button:not([disabled]), a[href]');
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      triggerButton?.focus();
    };
  }, [mobileMenuOpen]);

  const navigateTo = useCallback((menu, path, options = {}) => {
    if (menu) {
      setActiveMenu(menu);
    }
    if (options.resetCards) {
      localStorage.removeItem('selectedCardId');
      setCardsPageKey((key) => key + 1);
    }
    if (options.closeMobile) {
      setMobileMenuOpen(false);
    }
    navigate(path);
  }, [navigate]);

  const setPathCompat = useCallback((path) => {
    const currentSearch = window.location.pathname === path ? window.location.search : '';
    navigate(`${path}${currentSearch}`);
  }, [navigate]);

  const outletContext = useMemo(() => ({
    activeMenu,
    cardsPageKey,
    formatDate,
    navigateTo,
    setActiveMenu,
    setPathCompat,
    setUser,
    user,
  }), [activeMenu, cardsPageKey, navigateTo, setPathCompat, setUser, user]);

  return (
    <>
      <ToastContainer position="bottom-right" />
      <div className="main-layout">
        <aside className="sidebar">
          <BrandHeader className="sidebar-header sidebar-header-container" logoClassName="sidebar-logo" />
          <MainNavigation activeMenu={activeMenu} navigateTo={navigateTo} />
        </aside>

        <div className="content-area">
          <header className="topbar">
            <div className="topbar-row topbar-row-primary">
              <div className="mobile-topbar-brand" aria-label="Adabo Cash">
                <img src={adaboCashLogo} alt="" />
                <span><b>Adabo</b> Cash</span>
              </div>
              <div className="user-info user-info-container" onClick={() => setDropdownOpen((open) => !open)}>
                <div className="user-details">
                  <span className="user-name user-name-styled" style={{ color: getContrastYIQ('#f4f7fa') }}>
                    {user ? user.last_name : <span className="skeleton skeleton-text skeleton-width-80" />}
                  </span>
                </div>
                <span className="user-avatar">{user ? user.last_name?.[0]?.toUpperCase() : <span className="skeleton skeleton-avatar" />}</span>
                {dropdownOpen && (
                  <div className="user-dropdown">
                    {user && <div className="user-email user-email-display">{user.email}</div>}
                    <button
                      className="dropdown-item"
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate('/account-settings');
                        setDropdownOpen(false);
                      }}
                    >
                      <FiSettings className="dropdown-icon" />Paramètres du compte
                    </button>
                    <button
                      className="dropdown-item"
                      onClick={(event) => {
                        event.stopPropagation();
                        logout();
                      }}
                    >
                      <FiLogOut className="dropdown-icon" />Déconnexion
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="topbar-row topbar-row-balances">
              <div className="topbar-left">
                {user ? (
                  <div className="topbar-balance-container">
                    <BalanceItem label="$" value={user.usd_balance} />
                    <BalanceItem label="Ar" value={user.balance_mga} />
                    <BalanceItem label="USDT" value={user.balance_usdt} />
                  </div>
                ) : (
                  <span className="skeleton skeleton-text skeleton-width-60" />
                )}
              </div>
            </div>
          </header>

          <main className={`main-content${isMoneyFlowPage ? ' money-flow-main-content' : ''}`}>
            <Outlet context={outletContext} />
          </main>
        </div>

        <nav className="mobile-bottom-nav" aria-label="Navigation principale">
          <MobileNavButton active={activeMenu === 'dashboard'} icon={<FiHome />} label="Accueil" onClick={() => navigateTo('dashboard', '/dashboard')} />
          <MobileNavButton active={activeMenu === 'deposit'} icon={<FiTrendingUp />} label="Dépôt" onClick={() => navigateTo('deposit', '/deposit')} />
          <MobileNavButton active={activeMenu === 'exchange'} icon={<MdSwapHoriz />} label="Échange" onClick={() => navigateTo('exchange', '/exchange')} />
          <MobileNavButton active={activeMenu === 'cards'} icon={<FiCreditCard />} label="Cartes" onClick={() => navigateTo('cards', '/cards', { resetCards: true })} />
          <button
            ref={mobileMoreButtonRef}
            type="button"
            className={isMoreActive ? 'active' : ''}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-more-sheet"
            onClick={() => setMobileMenuOpen(true)}
          >
            <span className="mobile-nav-icon"><FiMoreHorizontal /></span>
            <span>Plus</span>
          </button>
        </nav>

        {mobileMenuOpen && (
          <div className="mobile-more-layer">
            <button className="mobile-more-backdrop" type="button" aria-label="Fermer le menu" onClick={() => setMobileMenuOpen(false)} />
            <section
              ref={mobileMoreSheetRef}
              id="mobile-more-sheet"
              className="mobile-more-sheet"
              role="dialog"
              aria-modal="true"
              aria-labelledby="mobile-more-title"
            >
              <div className="mobile-sheet-handle" aria-hidden="true" />
              <div className="mobile-more-header">
                <div>
                  <h2 id="mobile-more-title">Plus d'options</h2>
                </div>
                <button ref={mobileMoreCloseRef} type="button" className="mobile-more-close" aria-label="Fermer" onClick={() => setMobileMenuOpen(false)}>
                  <FiX />
                </button>
              </div>

              <div className="mobile-more-grid">
                {SHOW_USDT_WITHDRAW_MENU && (
                  <MobileMoreAction icon={<FiArrowUpCircle />} title="Retrait USDT" description="Envoyer vos fonds" active={activeMenu === 'withdraw'} onClick={() => navigateTo('withdraw', '/withdraw', { closeMobile: true })} />
                )}
                <MobileMoreAction icon={<FiRefreshCw />} title="Transactions" description="Voir votre historique" active={activeMenu === 'transactions'} onClick={() => navigateTo('transactions', '/transactions', { closeMobile: true })} />
                <MobileMoreAction icon={<FiUser />} title="Aide et FAQ" description="Obtenir de l'aide" active={activeMenu === 'contact'} onClick={() => navigateTo('contact', '/contact', { closeMobile: true })} />
                <MobileMoreAction icon={<FiSettings />} title="Paramètres" description="Gérer votre compte" active={activeMenu === 'settings'} onClick={() => navigateTo('settings', '/account-settings', { closeMobile: true })} />
              </div>
            </section>
          </div>
        )}
      </div>
    </>
  );
}

function MobileNavButton({ active, icon, label, onClick }) {
  return (
    <button type="button" className={active ? 'active' : ''} aria-current={active ? 'page' : undefined} onClick={onClick}>
      <span className="mobile-nav-icon">{icon}</span>
      <span>{label}</span>
    </button>
  );
}

function MobileMoreAction({ active, description, icon, onClick, title }) {
  return (
    <button type="button" className={active ? 'active' : ''} aria-current={active ? 'page' : undefined} onClick={onClick}>
      <span className="mobile-more-action-icon">{icon}</span>
      <span className="mobile-more-action-copy">
        <strong>{title}</strong>
        <small>{description}</small>
      </span>
    </button>
  );
}

function MainNavigation({ activeMenu, navigateTo }) {
  return (
    <nav className="sidebar-nav">
      <button type="button" className={activeMenu === 'dashboard' ? 'active' : ''} onClick={() => navigateTo('dashboard', '/dashboard')}><FiHome className="nav-icon" />Accueil</button>
      <button type="button" className={activeMenu === 'deposit' ? 'active' : ''} onClick={() => navigateTo('deposit', '/deposit')}><FiTrendingUp className="nav-icon-large" />Dépôt</button>
      {SHOW_USDT_WITHDRAW_MENU && (
        <button type="button" className={activeMenu === 'withdraw' ? 'active' : ''} onClick={() => navigateTo('withdraw', '/withdraw')}><FiArrowUpCircle className="nav-icon" />Retrait USDT</button>
      )}
      <button type="button" className={activeMenu === 'exchange' ? 'active' : ''} onClick={() => navigateTo('exchange', '/exchange')}><MdSwapHoriz className="nav-icon-medium" />Échange</button>
      <button type="button" className={activeMenu === 'cards' ? 'active' : ''} onClick={() => navigateTo('cards', '/cards', { resetCards: true })}><FiCreditCard className="nav-icon" />Cartes</button>
      <button type="button" className={activeMenu === 'transactions' ? 'active' : ''} onClick={() => navigateTo('transactions', '/transactions')}><FiRefreshCw className="nav-icon" />Transactions</button>
      <button type="button" className={activeMenu === 'contact' ? 'active' : ''} onClick={() => navigateTo('contact', '/contact')}><FiUser className="nav-icon" />Contact et FAQ</button>
    </nav>
  );
}

function BrandHeader({ className, logoClassName }) {
  return (
    <div className={className}>
      <img src={adaboCashLogo} alt="Adabo Cash" className={logoClassName} />
      <span className="sidebar-brand">
        <span className="sidebar-brand-adabo">Adabo</span>
        <span className="sidebar-brand-cash">Cash</span>
      </span>
    </div>
  );
}

function BalanceItem({ label, value }) {
  return (
    <span className="topbar-balance topbar-balance-item">
      <span className="topbar-balance-texts">
        <span className="topbar-balance-label topbar-balance-symbol">{label}</span>
        <span className="topbar-balance-amount">{formatAmount(value)}</span>
      </span>
    </span>
  );
}
